/**
 * Order and order-item lifecycles.
 *
 * WHY two machines instead of one:
 * An order's status is a *summary* of its items ("một phần món đã ra" =
 * PARTIALLY_SERVED), while each item has its own independent trip through the
 * kitchen. Modelling them separately is what lets a guest cancel one dish
 * without touching the rest of the bill, and lets the order status be
 * recomputed from item facts after any concurrent change.
 *
 * The status strings here mirror the `CHECK (status IN (...))` constraints in
 * `database/migrations/0006_orders.sql` exactly. If they ever drift, D1 rejects
 * the write — which is the safety net, not the design.
 */

import { StateMachine } from './state-machine.js';
import type { Transition } from './state-machine.js';

/**
 * Order statuses, in lifecycle order.
 *
 * Exported as a tuple so seed scripts, Zod schemas and the Admin filter
 * dropdowns can enumerate them without re-typing the list (a re-typed list is
 * how a status silently stops matching the DB constraint).
 */
export const ORDER_STATUSES = [
  'DRAFT',
  'PENDING_CONFIRMATION',
  'CONFIRMED',
  'IN_PRODUCTION',
  'PARTIALLY_SERVED',
  'SERVED',
  'COMPLETED',
  'CANCELLED',
  'VOIDED',
] as const;

/** Union of the order statuses stored in `orders.status`. */
export type OrderStatus = (typeof ORDER_STATUSES)[number];

/**
 * Order-level events.
 *
 * These are business intents, not CRUD verbs, because the audit log stores the
 * event name: "CONFIRM" tells an auditor what a manager did, "UPDATE" does not.
 */
export const ORDER_EVENTS = [
  'SUBMIT',
  'CONFIRM',
  'SEND_TO_STATION',
  'ITEM_READY',
  'SERVE_ALL',
  'PAY',
  'CANCEL',
  'VOID',
  'REOPEN',
] as const;

/** Union of order-level events. */
export type OrderEvent = (typeof ORDER_EVENTS)[number];

/**
 * The order lifecycle graph.
 *
 * Notable modelling decisions:
 * - SUBMIT is unguarded: a guest scanning the table QR is not a staff user and
 *   holds no permission, yet must be able to push their basket to the kitchen
 *   queue. Everything they submit lands in PENDING_CONFIRMATION so a human
 *   still approves it (`branch_qr_settings.require_staff_confirm`).
 * - DRAFT can go straight to CONFIRMED: a cashier keying an order at the POS is
 *   the confirmation; forcing them through PENDING_CONFIRMATION would add a tap
 *   to the busiest screen in the restaurant.
 * - CONFIRMED can go straight to SERVED for orders with nothing to cook
 *   (bottled drinks, packaged goods) — they never enter production.
 * - VOID is separated from CANCEL because it applies *after* the food left the
 *   pass: it must produce a loss record, whereas an early CANCEL must not.
 */
export const ORDER_TRANSITIONS: readonly Transition<OrderStatus, OrderEvent>[] = [
  { from: 'DRAFT', event: 'SUBMIT', to: 'PENDING_CONFIRMATION' },
  { from: 'DRAFT', event: 'CONFIRM', to: 'CONFIRMED', permission: 'order.confirm' },
  { from: 'PENDING_CONFIRMATION', event: 'CONFIRM', to: 'CONFIRMED', permission: 'order.confirm' },

  { from: 'CONFIRMED', event: 'SEND_TO_STATION', to: 'IN_PRODUCTION' },
  { from: 'IN_PRODUCTION', event: 'SEND_TO_STATION', to: 'IN_PRODUCTION', guardName: 'additionalRound' },
  { from: 'PARTIALLY_SERVED', event: 'SEND_TO_STATION', to: 'IN_PRODUCTION', guardName: 'additionalRound' },

  // ITEM_READY is a *projection* trigger: the order re-derives itself whenever a
  // dish reaches the pass. It is a self-loop on PARTIALLY_SERVED because the
  // second, third ... dish must not change the order status again.
  { from: 'IN_PRODUCTION', event: 'ITEM_READY', to: 'PARTIALLY_SERVED' },
  { from: 'PARTIALLY_SERVED', event: 'ITEM_READY', to: 'PARTIALLY_SERVED' },

  { from: 'CONFIRMED', event: 'SERVE_ALL', to: 'SERVED', guardName: 'noProductionItems' },
  { from: 'IN_PRODUCTION', event: 'SERVE_ALL', to: 'SERVED' },
  { from: 'PARTIALLY_SERVED', event: 'SERVE_ALL', to: 'SERVED' },

  // Payment closes the order. PARTIALLY_SERVED is allowed because guests do pay
  // and leave while a last dessert is still coming; `payment_status` on the row
  // is the authority on money, this status only says the order is finished.
  { from: 'SERVED', event: 'PAY', to: 'COMPLETED', permission: 'payment.receive' },
  { from: 'PARTIALLY_SERVED', event: 'PAY', to: 'COMPLETED', permission: 'payment.receive', guardName: 'confirmUnservedItems' },

  { from: 'DRAFT', event: 'CANCEL', to: 'CANCELLED', permission: 'order.cancel' },
  { from: 'PENDING_CONFIRMATION', event: 'CANCEL', to: 'CANCELLED', permission: 'order.cancel' },
  { from: 'CONFIRMED', event: 'CANCEL', to: 'CANCELLED', permission: 'order.cancel', guardName: 'requiresReason' },
  { from: 'IN_PRODUCTION', event: 'CANCEL', to: 'CANCELLED', permission: 'order.cancel', guardName: 'requiresReason' },

  { from: 'PARTIALLY_SERVED', event: 'VOID', to: 'VOIDED', permission: 'order.cancel', guardName: 'requiresReason' },
  { from: 'SERVED', event: 'VOID', to: 'VOIDED', permission: 'order.cancel', guardName: 'requiresReason' },
  { from: 'COMPLETED', event: 'VOID', to: 'VOIDED', permission: 'order.cancel', guardName: 'requiresReasonAndRefund' },

  // Reopening a completed order (guest orders one more beer) is an edit, not a
  // cancellation — but it must be blocked once an invoice has been issued.
  { from: 'COMPLETED', event: 'REOPEN', to: 'SERVED', permission: 'order.edit', guardName: 'noIssuedInvoice' },
] as const;

/**
 * The shared order lifecycle. Import this rather than the raw table so every
 * layer gets the same duplicate-edge validation and the same error messages.
 */
export const orderMachine = new StateMachine<OrderStatus, OrderEvent>(ORDER_TRANSITIONS, {
  name: 'đơn hàng',
  terminalStates: ['CANCELLED', 'VOIDED'],
});

/**
 * Order-item statuses, mirroring the `order_items.status` CHECK constraint.
 *
 * Note `PENDING` (not `PENDING_CONFIRMATION`): the DB uses the short form for
 * items. Keeping the mismatch rather than "tidying" it is deliberate — the
 * constant must match the constraint, not our aesthetic preference.
 */
export const ORDER_ITEM_STATUSES = [
  'DRAFT',
  'PENDING',
  'CONFIRMED',
  'SENT_TO_STATION',
  'IN_PRODUCTION',
  'READY',
  'SERVED',
  'CANCELLED',
  'RETURNED',
  'VOIDED',
] as const;

/** Union of the statuses stored in `order_items.status`. */
export type OrderItemStatus = (typeof ORDER_ITEM_STATUSES)[number];

/**
 * Item-level events.
 *
 * They intentionally overlap with the production machine (START/READY) because
 * a KDS action must move both the production ticket item and the order item;
 * sharing the vocabulary keeps the two projections readable side by side in the
 * audit log.
 */
export const ORDER_ITEM_EVENTS = [
  'SUBMIT',
  'CONFIRM',
  'SEND_TO_STATION',
  'START',
  'READY',
  'SERVE',
  'RETURN',
  'REMAKE',
  'CANCEL',
  'VOID',
] as const;

/** Union of order-item events. */
export type OrderItemEvent = (typeof ORDER_ITEM_EVENTS)[number];

/**
 * The order-item lifecycle graph.
 *
 * WHY cancelling is permitted right up to SERVED: staff genuinely need to void
 * a dish the guest rejected at the table. The *cost* of that late cancel is
 * handled by {@link cancelStageOf}, not by forbidding it — an unforbidden but
 * fully-accounted cancel is how you avoid staff working around the system.
 */
export const ORDER_ITEM_TRANSITIONS: readonly Transition<OrderItemStatus, OrderItemEvent>[] = [
  { from: 'DRAFT', event: 'SUBMIT', to: 'PENDING' },
  { from: 'DRAFT', event: 'CONFIRM', to: 'CONFIRMED', permission: 'order.confirm' },
  { from: 'PENDING', event: 'CONFIRM', to: 'CONFIRMED', permission: 'order.confirm' },

  { from: 'CONFIRMED', event: 'SEND_TO_STATION', to: 'SENT_TO_STATION' },
  { from: 'SENT_TO_STATION', event: 'START', to: 'IN_PRODUCTION' },
  { from: 'IN_PRODUCTION', event: 'READY', to: 'READY' },
  // Items that need no preparation (canned drinks) jump the queue: the ticket is
  // printed for stock traceability but the station marks them ready immediately.
  { from: 'SENT_TO_STATION', event: 'READY', to: 'READY', guardName: 'noProductionRequired' },
  { from: 'CONFIRMED', event: 'READY', to: 'READY', guardName: 'noProductionRequired' },

  { from: 'READY', event: 'SERVE', to: 'SERVED' },
  { from: 'SERVED', event: 'RETURN', to: 'RETURNED', permission: 'kitchen.return', guardName: 'requiresReason' },
  { from: 'READY', event: 'RETURN', to: 'RETURNED', permission: 'kitchen.return', guardName: 'requiresReason' },

  // A remake re-enters production on the SAME item row so KPI and cost stay
  // attached to the original sale line; `remake_count` records the repetition.
  { from: 'RETURNED', event: 'REMAKE', to: 'SENT_TO_STATION', permission: 'kitchen.remake', guardName: 'requiresReason' },

  { from: 'DRAFT', event: 'CANCEL', to: 'CANCELLED', permission: 'order.item.cancel' },
  { from: 'PENDING', event: 'CANCEL', to: 'CANCELLED', permission: 'order.item.cancel' },
  { from: 'CONFIRMED', event: 'CANCEL', to: 'CANCELLED', permission: 'order.item.cancel' },
  { from: 'SENT_TO_STATION', event: 'CANCEL', to: 'CANCELLED', permission: 'order.item.cancel', guardName: 'requiresReason' },
  { from: 'IN_PRODUCTION', event: 'CANCEL', to: 'CANCELLED', permission: 'order.item.cancel', guardName: 'requiresReason' },
  { from: 'READY', event: 'CANCEL', to: 'CANCELLED', permission: 'order.item.cancel', guardName: 'requiresReason' },
  { from: 'RETURNED', event: 'CANCEL', to: 'CANCELLED', permission: 'order.item.cancel', guardName: 'requiresReason' },

  { from: 'SERVED', event: 'VOID', to: 'VOIDED', permission: 'order.cancel', guardName: 'requiresReason' },
] as const;

/**
 * The shared order-item lifecycle.
 * `VOIDED` and `CANCELLED` are terminal: an item that left the bill never comes
 * back, a replacement is always a new line.
 */
export const orderItemMachine = new StateMachine<OrderItemStatus, OrderItemEvent>(ORDER_ITEM_TRANSITIONS, {
  name: 'món',
  terminalStates: ['CANCELLED', 'VOIDED'],
});

/**
 * When a cancellation happens relative to production — the single fact that
 * decides whether ingredients go back on the shelf.
 *
 * Mirrors `order_items.cancel_stage`.
 */
export type CancelStage = 'BEFORE_PRODUCTION' | 'DURING_PRODUCTION' | 'AFTER_PRODUCTION';

/**
 * Classify a cancellation by the item's status at the moment it was cancelled.
 *
 * WHY this drives inventory rather than a manual choice: staff cannot be asked
 * "did the kitchen already start?" — they will guess, and the guess becomes
 * phantom stock. The item status is an observed fact, so deriving the stage
 * from it makes stock movements reproducible and auditable.
 *
 * - BEFORE_PRODUCTION — nothing was consumed; restore the reservation in full.
 * - DURING_PRODUCTION — ingredients are in the pan; write off as wastage.
 * - AFTER_PRODUCTION  — a finished dish is thrown away; write off AND record a
 *   loss with a responsible party.
 */
export function cancelStageOf(itemStatus: OrderItemStatus): CancelStage {
  switch (itemStatus) {
    case 'DRAFT':
    case 'PENDING':
    case 'CONFIRMED':
    case 'SENT_TO_STATION':
      // The ticket may be printed, but no station has accepted it: stock is
      // only reserved, never issued.
      return 'BEFORE_PRODUCTION';
    case 'IN_PRODUCTION':
      return 'DURING_PRODUCTION';
    case 'READY':
    case 'SERVED':
    case 'RETURNED':
    case 'VOIDED':
      return 'AFTER_PRODUCTION';
    case 'CANCELLED':
      // Already cancelled. Re-deriving must be idempotent and must NOT create a
      // second wastage record, so the cheapest stage is returned deliberately.
      return 'BEFORE_PRODUCTION';
  }
}

/**
 * Does cancelling at this stage consume ingredients?
 *
 * Exposed separately because the inventory service should not have to know the
 * stage vocabulary — it only needs the yes/no.
 */
export function cancelConsumesStock(stage: CancelStage): boolean {
  return stage !== 'BEFORE_PRODUCTION';
}

/** Order statuses that still occupy a table and belong in the realtime working set. */
export function isOrderOpen(status: OrderStatus): boolean {
  return !orderMachine.isTerminal(status) && status !== 'COMPLETED';
}

/** Item statuses that the kitchen still owes the guest — the basis of PARTIALLY_SERVED. */
export function isItemOutstanding(status: OrderItemStatus): boolean {
  switch (status) {
    case 'DRAFT':
    case 'PENDING':
    case 'CONFIRMED':
    case 'SENT_TO_STATION':
    case 'IN_PRODUCTION':
    case 'READY':
    case 'RETURNED':
      return true;
    case 'SERVED':
    case 'CANCELLED':
    case 'VOIDED':
      return false;
  }
}
